import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Form, Select, Spin, message } from "antd";
import dayjs from "dayjs";
import "./AdminTools.css";
import { useAuth } from "../AuthProvider";
import APIService from "../../Service/APIService";

const DATE_TIME_FORMAT = "MM-DD-YYYY hh:mm A";

const ContractRightsSettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const userType = user?.Type;
  const isSuperAdmin = !!user?.IsSuperAdmin;
  const apiServiceRef = React.useRef(new APIService());
  const [form] = Form.useForm();
  const selectedHouseDistributorID = Form.useWatch("HouseDistributorID", form);

  const [settings, setSettings] = React.useState(null);
  const [distributors, setDistributors] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  const getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || fallback;

  const formatDateTime = (value) => {
    if (!value) return "-";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format(DATE_TIME_FORMAT) : "-";
  };

  const fetchSettings = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const [settingsResponse, distributorsResponse] = await Promise.all([
        apiServiceRef.current.getDataFromBackend("getcontractrightssettings"),
        apiServiceRef.current.getDataFromBackend("getdistributors"),
      ]);

      const nextSettings = settingsResponse?.data?.data || null;
      setSettings(nextSettings);
      setDistributors(distributorsResponse?.data?.data || []);
    } catch (error) {
      message.error(
        getErrorMessage(error, "Failed to load contract rights settings")
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    form.setFieldsValue({
      HouseDistributorID: settings?.HouseDistributorID ?? null,
    });
  }, [form, settings]);

  React.useEffect(() => {
    if (!userType) return;

    if (userType !== "Admin" || !isSuperAdmin) {
      navigate("/");
      return;
    }

    fetchSettings();
  }, [fetchSettings, isSuperAdmin, navigate, userType]);

  const distributorOptions = React.useMemo(
    () =>
      distributors.map((distributor) => ({
        value: distributor.ID,
        label: distributor.DistributorName,
      })),
    [distributors]
  );

  const hasChanged =
    selectedHouseDistributorID != null &&
    Number(selectedHouseDistributorID) !== Number(settings?.HouseDistributorID);

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const response = await apiServiceRef.current.updateDataToBackend(
        { HouseDistributorID: values.HouseDistributorID },
        "updatecontractrightssettings"
      );

      const nextSettings = response?.data?.data || null;
      setSettings(nextSettings);
      form.setFieldsValue({
        HouseDistributorID: nextSettings?.HouseDistributorID ?? null,
      });
      message.success("Contract rights settings updated successfully");
    } catch (error) {
      if (error?.errorFields) return;
      message.error(
        getErrorMessage(error, "Unable to save contract rights settings")
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-tools-wrapper">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div className="text text-h1">Contract Rights Settings</div>
          <div className="text text-smallText admin-tools-subtitle">
            Manage distributor settings used by contract rights workflows.
          </div>
        </div>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/AdminTools")}>
          Back to Admin Tools
        </Button>
      </div>

      <div className="admin-tools-row">
        <div className="admin-tools-banner-alerts">
          <div className="admin-tools-banner-alerts-header">
            <div>
              <div className="text text-h2">House Distributor</div>
              <div className="text text-smallText admin-tools-subtitle">
                Current: {settings?.HouseDistributorName || "-"}
              </div>
              <div className="text text-smallText admin-tools-subtitle">
                Last updated: {formatDateTime(settings?.updatedAt)}
              </div>
            </div>
          </div>

          <Spin spinning={isLoading}>
            <Form form={form} layout="vertical">
              <Form.Item
                className="contract-rights-distributor-field"
                label="House Distributor"
                name="HouseDistributorID"
                rules={[
                  {
                    required: true,
                    message: "House Distributor is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={distributorOptions}
                  placeholder="Select House Distributor"
                  disabled={isLoading || isSaving}
                />
              </Form.Item>

              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={isSaving}
                disabled={isLoading || isSaving || !hasChanged}
              >
                Save
              </Button>
            </Form>
          </Spin>
        </div>
      </div>
    </div>
  );
};

export default ContractRightsSettings;
